import React, { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  MONTH_NAMES, YEAR_COLORS, formatMWh, formatAUD,
  useContainerWidth, EmptyState,
  defaultLineLegendProps, tickLabelStyle, toggleButtonGroupSx,
} from './shared';

/**
 * Year-over-Year Chart
 *
 * Monthly generation or revenue overlaid by year (one line per year).
 * Reveals multi-year trends, seasonal patterns, and the impact of
 * coal retirements, renewable additions, and weather events.
 *
 * Uses the full 10-year dataset (2015–2025+).
 */
const YoYChart = ({ generationData, revenueData }) => {
  const { ref: genRef, width: genWidth } = useContainerWidth();
  const { ref: revRef, width: revWidth } = useContainerWidth();
  const [metric, setMetric] = useState('generation');

  const activeData = metric === 'generation' ? generationData : revenueData;

  if (!activeData || activeData.length === 0) return <EmptyState message="No year-over-year data available" />;

  const years = [...new Set(activeData.map((d) => d.year))].sort();
  const xLabels = MONTH_NAMES;

  const series = years.map((year, idx) => {
    const yearData = activeData.filter((d) => d.year === year);
    const monthMap = {};
    yearData.forEach((d) => {
      monthMap[d.month] = metric === 'generation' ? d.total_mwh : d.total_revenue_aud;
    });

    return {
      type: 'line',
      label: String(year),
      data: Array.from({ length: 12 }, (_, i) => monthMap[i + 1] ?? null),
      color: YEAR_COLORS[idx % YEAR_COLORS.length],
      curve: 'monotone',
      connectNulls: true,
    };
  });

  const ref = metric === 'generation' ? genRef : revRef;
  const w = metric === 'generation' ? genWidth : revWidth;
  const formatter = metric === 'generation' ? formatMWh : formatAUD;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={metric}
          exclusive
          onChange={(_, v) => { if (v) setMetric(v); }}
          size="small"
          sx={toggleButtonGroupSx}
        >
          <ToggleButton value="generation">Generation</ToggleButton>
          <ToggleButton value="revenue">Revenue</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
        {w > 0 && (
          <LineChart
            width={w}
            height={420}
            series={series}
            xAxis={[{
              scaleType: 'point',
              data: xLabels,
              tickLabelStyle: { fontSize: 12, fill: '#5a6a7e' },
            }]}
            yAxis={[{ valueFormatter: formatter, tickLabelStyle }]}
            margin={{ top: 20, right: 20, left: 80, bottom: 60 }}
            slotProps={{ legend: { ...defaultLineLegendProps } }}
          />
        )}
      </Box>
    </Box>
  );
};

export default YoYChart;
